import secureLocalStorage from "react-secure-storage";

export const AUTH_STORAGE_KEYS = {
  auth: "auth",
  user: "user",
  token: "token",
  refreshToken: "refreshToken",
};

const parseJsonSafely = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const unwrapLegacyUserWrapper = (value) => {
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    value.user &&
    typeof value.user === "object" &&
    !Array.isArray(value.user) &&
    !value.token &&
    !value.accessToken &&
    !value.refreshToken
  ) {
    return value.user;
  }

  return value;
};

export const normalizeAuthSession = (value) => {
  const parsedValue = unwrapLegacyUserWrapper(parseJsonSafely(value));

  if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
    return null;
  }

  const nestedUser =
    parsedValue.user && typeof parsedValue.user === "object" && !Array.isArray(parsedValue.user)
      ? parsedValue.user
      : parsedValue.userDetails &&
          typeof parsedValue.userDetails === "object" &&
          !Array.isArray(parsedValue.userDetails)
        ? parsedValue.userDetails
        : null;

  const accessToken =
    parsedValue.accessToken ||
    parsedValue.token ||
    nestedUser?.accessToken ||
    nestedUser?.token ||
    parsedValue.refreshToken ||
    nestedUser?.refreshToken ||
    "";

  const refreshToken =
    parsedValue.refreshToken ||
    nestedUser?.refreshToken ||
    parsedValue.accessToken ||
    parsedValue.token ||
    nestedUser?.accessToken ||
    nestedUser?.token ||
    "";

  const user = nestedUser || parsedValue.userDetails || parsedValue;

  return {
    ...parsedValue,
    accessToken,
    token: accessToken,
    refreshToken,
    user,
    userDetails: parsedValue.userDetails || user,
  };
};

export const hasAuthTokens = (session) =>
  Boolean(session?.accessToken || session?.refreshToken || session?.token);

export const getStoredAuthSession = () => {
  const storedAuth = secureLocalStorage.getItem(AUTH_STORAGE_KEYS.auth);
  const normalizedAuth = normalizeAuthSession(storedAuth);

  const storedUser = secureLocalStorage.getItem(AUTH_STORAGE_KEYS.user);
  const normalizedUser = normalizeAuthSession(storedUser);
  const storedToken = secureLocalStorage.getItem(AUTH_STORAGE_KEYS.token);
  const storedRefreshToken = secureLocalStorage.getItem(
    AUTH_STORAGE_KEYS.refreshToken
  );

  const mergedSession = normalizeAuthSession({
    ...(normalizedUser || {}),
    ...(normalizedAuth || {}),
    token:
      normalizedAuth?.accessToken ||
      normalizedUser?.accessToken ||
      storedToken ||
      normalizedAuth?.refreshToken ||
      normalizedUser?.refreshToken ||
      storedRefreshToken ||
      "",
    refreshToken:
      normalizedAuth?.refreshToken ||
      normalizedUser?.refreshToken ||
      storedRefreshToken ||
      normalizedAuth?.accessToken ||
      normalizedUser?.accessToken ||
      storedToken ||
      "",
  });

  if (mergedSession) {
    return mergedSession;
  }

  return null;
};

export const persistAuthSession = (value) => {
  const normalizedSession = normalizeAuthSession(value);

  if (!normalizedSession) {
    return null;
  }

  secureLocalStorage.setItem(
    AUTH_STORAGE_KEYS.auth,
    JSON.stringify(normalizedSession)
  );
  secureLocalStorage.setItem(
    AUTH_STORAGE_KEYS.user,
    JSON.stringify({ user: normalizedSession })
  );
  secureLocalStorage.setItem(AUTH_STORAGE_KEYS.token, normalizedSession.accessToken);
  secureLocalStorage.setItem(
    AUTH_STORAGE_KEYS.refreshToken,
    normalizedSession.refreshToken
  );

  return normalizedSession;
};

export const clearStoredAuthSession = () => {
  secureLocalStorage.removeItem(AUTH_STORAGE_KEYS.auth);
  secureLocalStorage.removeItem(AUTH_STORAGE_KEYS.user);
  secureLocalStorage.removeItem(AUTH_STORAGE_KEYS.token);
  secureLocalStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
};
